import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Mail, Phone, MapPin, Clock, FolderKanban, Receipt, ShieldCheck, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Floating preview card that appears when hovering over a client row/card.
 *
 * Wraps arbitrary children (typically the client-name button). Only fires on
 * pointer devices — never on touch — so mobile taps work as expected without
 * triggering ghost cards.
 *
 * Data comes from a lightweight 360° fetch, cached in-memory per user_id so
 * re-hovering the same client is instant.
 */

const CACHE = new Map(); // user_id → { data, at }
const CACHE_TTL_MS = 60_000;

// Public reset — called at logout so shared-browser sessions don't leak
// cached 360° payloads across identities.
export function clearClientHoverCache() {
  CACHE.clear();
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
}

export default function ClientHoverCard({ client, enrich, children, delay = 300 }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [summary, setSummary] = useState(() => {
    const c = CACHE.get(client.user_id);
    return c && Date.now() - c.at < CACHE_TTL_MS ? c.data : null;
  });
  const timerRef = useRef(null);
  const anchorRef = useRef(null);

  // Detect touch-primary devices → disable hover entirely.
  const isTouch = typeof window !== "undefined" &&
    window.matchMedia && window.matchMedia("(hover: none)").matches;

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const openCard = () => {
    if (isTouch) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const W = 340;
      const H = 300;
      // Prefer right side; flip to left when it would overflow.
      let left = rect.right + 8;
      if (left + W > window.innerWidth - 8) left = Math.max(8, rect.left - W - 8);
      // Vertically clamp so the card stays inside the viewport.
      let top = rect.top;
      if (top + H > window.innerHeight - 8) top = Math.max(8, window.innerHeight - H - 8);
      setPos({ top, left });
      setOpen(true);
      const cached = CACHE.get(client.user_id);
      const fresh = cached && Date.now() - cached.at < CACHE_TTL_MS;
      if (!fresh) {
        api.get(`/admin/clients/${client.user_id}/360`)
          .then((r) => { CACHE.set(client.user_id, { data: r.data, at: Date.now() }); setSummary(r.data); })
          .catch(() => {
            const fallback = { projects: [], invoices: [], payments: [] };
            CACHE.set(client.user_id, { data: fallback, at: Date.now() });
            setSummary(fallback);
          });
      } else {
        setSummary(cached.data);
      }
    }, delay);
  };

  const closeCard = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(false), 100);
  };

  const isPending = client.access_level !== "approved";
  const openInv = enrich?.open_inv ?? summary?.invoices?.filter(
    (i) => !["paid", "void", "denied"].includes(i.status)
  ).length ?? 0;
  const projectCount = enrich?.project_count ?? summary?.projects?.length ?? 0;
  const activeProj = summary?.projects?.filter(
    (p) => !["completed", "cancelled"].includes(p.status)
  ).length ?? 0;
  const lifetimePaid = (summary?.payments || []).reduce((a, p) => a + (Number(p.amount) || 0), 0);

  return (
    <span
      ref={anchorRef}
      onMouseEnter={openCard}
      onMouseLeave={closeCard}
      onFocus={openCard}
      onBlur={closeCard}
      className="inline-block"
    >
      {children}
      {open && createPortal(
        <div
          data-testid={`client-hover-${client.user_id}`}
          onMouseEnter={() => clearTimeout(timerRef.current)}
          onMouseLeave={closeCard}
          className="fixed z-[100] w-[340px] rounded-xl border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95"
          style={{ top: pos.top, left: pos.left }}
        >
          {/* Header */}
          <div className="p-4 flex items-start gap-3 border-b border-[#E2E8F0] dark:border-slate-700">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0A2540] to-[#12365b] text-white grid place-items-center font-heading font-semibold uppercase shrink-0">
              {(client.name || client.email || "?").trim().charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-heading text-[15px] text-[#0A2540] dark:text-white truncate">
                  {client.name || "Unnamed"}
                </h4>
                {isPending ? (
                  <span className="inline-flex items-center gap-1 text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300">
                    <AlertCircle className="w-2.5 h-2.5" /> Pending
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                    <ShieldCheck className="w-2.5 h-2.5" /> Approved
                  </span>
                )}
              </div>
              {client.customer_code && (
                <div className="font-mono text-[11px] text-[#C9A356] mt-0.5">{client.customer_code}</div>
              )}
            </div>
          </div>

          {/* Contact block */}
          <div className="p-4 space-y-1.5 text-[12px] text-[#475569] dark:text-slate-400 border-b border-[#E2E8F0] dark:border-slate-700">
            {client.email && (
              <div className="flex items-center gap-1.5 min-w-0">
                <Mail className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{client.email}</span>
              </div>
            )}
            {client.phone && (
              <div className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 shrink-0" />
                <span>{client.phone}</span>
              </div>
            )}
            {client.address && (
              <div className="flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span className="line-clamp-2">{client.address}</span>
              </div>
            )}
            {client.created_at && (
              <div className="flex items-center gap-1.5 text-[#94A3B8]">
                <Clock className="w-3 h-3" /> Joined {timeAgo(client.created_at)}
              </div>
            )}
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-3 divide-x divide-[#E2E8F0] dark:divide-slate-700 text-center">
            <div className="p-3">
              <div className="inline-flex items-center justify-center gap-1 text-[9px] uppercase tracking-wider text-[#94A3B8]">
                <FolderKanban className="w-2.5 h-2.5" /> Projects
              </div>
              <div className="font-heading text-lg font-bold text-[#0A2540] dark:text-white tabular-nums leading-tight mt-0.5">
                {projectCount}
              </div>
              <div className="text-[10px] text-[#475569] dark:text-slate-400">{activeProj} active</div>
            </div>
            <div className="p-3">
              <div className="inline-flex items-center justify-center gap-1 text-[9px] uppercase tracking-wider text-[#94A3B8]">
                <Receipt className="w-2.5 h-2.5" /> Open inv
              </div>
              <div className={`font-heading text-lg font-bold tabular-nums leading-tight mt-0.5 ${openInv > 0 ? "text-amber-600" : "text-[#0A2540] dark:text-white"}`}>
                {openInv}
              </div>
              <div className="text-[10px] text-[#475569] dark:text-slate-400">outstanding</div>
            </div>
            <div className="p-3">
              <div className="text-[9px] uppercase tracking-wider text-[#94A3B8]">Lifetime</div>
              <div className="font-heading text-lg font-bold text-[#0A2540] dark:text-white tabular-nums leading-tight mt-0.5">
                ${lifetimePaid >= 1000 ? `${(lifetimePaid / 1000).toFixed(1)}k` : lifetimePaid.toFixed(0)}
              </div>
              <div className="text-[10px] text-[#475569] dark:text-slate-400">paid</div>
            </div>
          </div>

          {/* Latest project preview */}
          {summary?.projects?.length > 0 && (
            <div className="p-3 border-t border-[#E2E8F0] dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40">
              <div className="text-[9px] uppercase tracking-wider text-[#94A3B8] mb-1">Latest project</div>
              <div className="text-[12px] text-[#0A2540] dark:text-white truncate">
                {summary.projects[0].title}
              </div>
              <div className="text-[10px] text-[#475569] dark:text-slate-400 truncate">
                {(summary.projects[0].status || "").replace(/_/g, " ")}
                {summary.projects[0].amount ? ` · $${Number(summary.projects[0].amount).toLocaleString()}` : ""}
              </div>
            </div>
          )}

          <div className="px-4 py-2 text-[10px] text-[#94A3B8] text-center border-t border-[#E2E8F0] dark:border-slate-700">
            Click to open full profile
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
